import { Dispatch, DispatchWithoutAction } from "react";
import { Form } from "react-final-form";
import { CheckIcon } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import Dialog from "@/components/dialog/dialog";
import DialogHeader from "@/components/dialog/dailog-header";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { TextField } from "@/features/shared/components";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import DialogFooter from "@/components/dialog/dialog-footer";
import { ItemCardContent } from "@/features/widget-forms/forms/weather-model-map/components/item-card-content/item-card-content";
import styles from "@/features/widget-forms/forms/map-widget-editor/configure-layer-modal.module.css";

export interface SeriesDetails {
  label: string;
}

interface ConfigureSeriesModalProps {
  parameter: TimeSeriesParameterOption | undefined;
  open: boolean;
  onSubmit: Dispatch<SeriesDetails>;
  onCancel: DispatchWithoutAction;
  initialValues?: Partial<SeriesDetails>;
}

export function ConfigureSeriesModal({
  open,
  parameter,
  onSubmit,
  onCancel,
  initialValues,
}: ConfigureSeriesModalProps) {
  const { t } = useTranslation();
  return (
    <Dialog open={open}>
      <Dialog.Content className={dialogVariants.default}>
        <Form onSubmit={onSubmit}>
          {(formProps) => (
            <form onSubmit={formProps.handleSubmit}>
              <DialogHeader.Root>
                <DialogHeader.Title>
                  {t("admin.widgets.fields.model_map.configure_series_modal.title")}
                </DialogHeader.Title>
              </DialogHeader.Root>
              <h3 className={styles.header}>
                {t("admin.widgets.fields.model_map.configure_series_modal.preview_header")}
              </h3>
              {parameter ? (
                <div className={styles.layerPreview}>
                  <ItemCardContent
                    title={parameter.label}
                    subtitle={parameter.datasetLabel}
                  />
                </div>
              ) : null}
              <h3 className={styles.header}>
                {t("admin.widgets.fields.model_map.configure_series_modal.settings_header")}
              </h3>
              <TextField
                name="label"
                label={t(
                  "admin.widgets.fields.model_map.configure_series_modal.label.label",
                )}
                helperText={t(
                  "admin.widgets.fields.model_map.configure_series_modal.label.helper_text",
                )}
                autoFocus
                required
                defaultValue={initialValues?.label}
                className={styles.textField}
              />
              <DialogFooter>
                <Stack flexDirection="row" justifyContent={"flex-end"} gap={8}>
                  <Button type="button" variant="solid-secondary" onClick={onCancel}>
                    {t("general.cancel")}
                  </Button>
                  <Button type="submit" variant="solid">
                    <CheckIcon />
                    {t("general.done")}
                  </Button>
                </Stack>
              </DialogFooter>
            </form>
          )}
        </Form>
      </Dialog.Content>
    </Dialog>
  );
}
